import React, { useEffect, useMemo, useRef, useState } from "react";
import { PiFileLight } from "react-icons/pi";

const ext = (name: string) => (name.split(".").pop() || "").toLowerCase();

const useInView = (options?: IntersectionObserverInit) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        obs.unobserve(entry.target);
      }
    }, options);
    obs.observe(el);
    return () => obs.disconnect();
  }, [options]);
  return { ref, inView };
};

type ThumbProps = {
  url?: string;          
  name: string;         
  contentType?: string; 
  className?: string;
  fallbackIcon?: React.ReactNode;
  preferPdfPreview?: boolean; 
};

const Thumbnail: React.FC<ThumbProps> = ({ 
  url, 
  name, 
  contentType, 
  className, 
  fallbackIcon,
  preferPdfPreview = false 
}) => {
  const { ref, inView } = useInView({ rootMargin: "300px" });
  const [imgSrc, setImgSrc] = useState<string | null>(null);
  const [error, setError] = useState(false);

  const safeUrl = useMemo(() => {
    if (!url) return "";
    return url.includes(" ") ? url.replace(/ /g, "%20") : url;
  }, [url]);

  const e = ext(name);
  const isImage =
    (contentType && contentType.startsWith("image/")) ||
    ["png", "jpg", "jpeg", "gif", "webp", "svg"].includes(e);
  const isPdf = contentType === "application/pdf" || e === "pdf" || preferPdfPreview;

  useEffect(() => {
    if (!inView) return;
    let cancelled = false;

    if (isPdf) {
      (async () => {
        try {
          const pdfjs = await import("pdfjs-dist");

          try {
            pdfjs.GlobalWorkerOptions.workerSrc = new URL(
              "pdfjs-dist/build/pdf.worker.min.js",
              import.meta.url
            ).toString();
          } catch {
            pdfjs.GlobalWorkerOptions.workerSrc =
              "https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js";
          }

          const loadingTask = pdfjs.getDocument({ 
            url: safeUrl,
            httpHeaders: {
              'Access-Control-Allow-Origin': '*'
            }
          });
          
          const pdf = await loadingTask.promise;
          const page = await pdf.getPage(1);

          const viewport = page.getViewport({ scale: 0.5 }); 
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          if (!ctx) throw new Error("Canvas 2D context unavailable");

          canvas.width = viewport.width;
          canvas.height = viewport.height;

          const renderContext = {
            canvasContext: ctx,
            viewport: viewport
          };

          await page.render(renderContext).promise;
          
          if (!cancelled) {
            const thumbnailDataUrl = canvas.toDataURL("image/png", 0.8);
            setImgSrc(thumbnailDataUrl);
          }
        } catch (err) {
          if (!cancelled) {
            console.warn("[Thumbnail] PDF render failed:", err);
            setError(true);
          }
        }
      })();

      return () => {
        cancelled = true;
      };
    }

    if (isImage) {
      setImgSrc(safeUrl);
      return;
    }

    setError(true);
  }, [inView, isImage, isPdf, safeUrl, preferPdfPreview]);

  return (
    <div
      ref={ref}
      className={`relative overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-700 ${className || ""}`}
      style={{ aspectRatio: "4 / 3" }}
    >
      {!imgSrc && !error && (
        <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-gray-200 to-gray-300 dark:from-gray-600 dark:to-gray-700" />
      )}

      {imgSrc && (
        <img
          src={imgSrc}
          alt={`${name} preview`}
          loading="lazy"
          crossOrigin="anonymous"
          onError={() => {
            console.warn("[Thumbnail] Preview image failed to load:", safeUrl);
            setImgSrc(null);
            setError(true);
          }}
          className="absolute inset-0 h-full w-full object-cover object-top"
        />
      )}

      {error && (
        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800">
          {fallbackIcon || <PiFileLight className="h-8 w-8 text-gray-500" />}
        </div>
      )}
    </div>
  );
};

export default Thumbnail;